import { useCallback, useEffect, useRef, useState } from "react";

interface QueuedEvent {
  eventName: string;
  eventType: string;
  testToken: string;
  timestamp: Date;
  extraData?: Record<string, unknown>;
}

export const useEventQueue = () => {
  const [queue, setQueue] = useState<QueuedEvent[]>([]);
  const isProcessing = useRef(false);
  const trackEventRef = useRef<(event: QueuedEvent) => Promise<void>>();

  const enqueueEvent = useCallback((event: QueuedEvent) => {
    setQueue((prevQueue) => {
      const newQueue = [...prevQueue, event];
      return newQueue;
    });
  }, []);

  const processNextEvent = useCallback(async () => {
    if (isProcessing.current || queue.length === 0 || !trackEventRef.current) {
      return;
    }

    isProcessing.current = true;
    const nextEvent = queue[0];

    await trackEventRef.current(nextEvent);
    setQueue((prevQueue) => {
      const newQueue = prevQueue.slice(1);
      return newQueue;
    });
    isProcessing.current = false;
  }, [queue]);

  useEffect(() => {
    if (!isProcessing.current && queue.length > 0) {
      processNextEvent();
    }
  }, [queue, processNextEvent]);

  const setTrackEventFunction = useCallback((trackFn: (event: QueuedEvent) => Promise<void>) => {
    trackEventRef.current = trackFn;
  }, []);

  return {
    enqueueEvent,
    setTrackEventFunction,
    queueLength: queue.length,
    queue,
  };
};
